import { useState, useEffect, useMemo, useCallback, Suspense } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getJson, getJsonUnwrapped, http, unwrapPaginatedResponse } from "@/lib/http";
import API_BASE from "@/lib/apiBase";
import { useLocation } from "wouter";
import { Plus, Smartphone, Edit, Trash2, Building2, Upload, Download, FileText, AlertCircle, CheckCircle2, X, Calendar, ArrowLeft, Eye, DollarSign, Package, ChevronDown, ChevronUp, ChevronRight, AlertTriangle, Settings, Lock, Unlock, Loader2, BarChart3, Archive, ArchiveRestore } from "lucide-react";
import { DataTable } from "@/components/tables/data-table";
import { createBookingCycleColumns } from "@/components/tables/booking-cycle-columns";
import { createPackColumns } from "@/components/tables/pack-columns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { PackEditorDialog } from "@/components/packs/PackEditorDialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PackStatusBadge } from "@/components/packs/PackStatusBadge";
import type { Retailer, StoreFormat, Store as StoreType, InsertRetailer, InsertStoreFormat, InsertStore, MediaFormat, InsertMediaFormat, StoreMediaRequirement, InsertStoreMediaRequirement, User, BookingCycle, InsertBookingCycle, Pack, InsertPack, CycleRateTemplate, InsertCycleRateTemplate, RateTemplate, InsertRateTemplate, StoreFormatOperatorAssignment, Task, PackTaskAssignment, FormatGroupItem, AcceptableFormatGroups } from "@shared/schema";
import { usePackStatus } from "@/hooks/usePackStatus";
import { YearFilter } from "@/components/filters/YearFilter";
import { useYear } from "@/state/yearStore";

function PackLockStatusBadge({ packId }: { packId: number }) {
  const { status, isLoading, isError } = usePackStatus(packId);

  if (isLoading) return <Badge variant="outline">Loading...</Badge>;
  if (isError) return <Badge variant="destructive">Error</Badge>;

  if (status === 'locked') {
    return <Badge className="bg-green-100 text-green-800">Locked</Badge>;
  }
  return <Badge variant="secondary">Unlocked</Badge>;
}

function PackLockToggleButton({ pack }: { pack: Pack }) {
  const { status, lock, unlock, isLocking, isUnlocking } = usePackStatus(pack.id);
  const isPending = isLocking || isUnlocking;

  if (status === 'locked') {
    return (
      <Button variant="outline" size="sm" onClick={() => unlock()} disabled={isPending}>
        {isPending ? 'Unlocking...' : 'Unlock'}
      </Button>
    );
  }
  return (
    <Button variant="outline" size="sm" onClick={() => lock()} disabled={isPending}>
      {isPending ? 'Locking...' : 'Lock'}
    </Button>
  );
}

function PackEditButton({ pack, onEdit }: { pack: Pack; onEdit: (pack: Pack, mode: 'view' | 'edit') => void }) {
  const { status } = usePackStatus(pack.id);

  const handleClick = () => {
    const mode = status === 'locked' ? 'view' : 'edit';
    onEdit(pack, mode);
  };

  return (
    <Button variant="outline" size="sm" onClick={handleClick}>
      {status === 'locked' ? <Eye className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
    </Button>
  );
}

// Extended Pack type with version information
type PackWithVersion = Pack & {
  latestVersion?: {
    id: number;
    version: number;
    locked_at: Date;
    locked_by_user_id: number;
  };
};

// =================================================================================
// RetailerPacksTab Component
// =================================================================================

export interface RetailerPacksTabProps {
  retailerId: number;
}

export function RetailerPacksTab({ retailerId }: RetailerPacksTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  // State for pack dialogs and forms
  const [isPackDialogOpen, setIsPackDialogOpen] = useState(false);
  const [packDialogMode, setPackDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedPack, setSelectedPack] = useState<Pack | null>(null);
  const [packToDelete, setPackToDelete] = useState<{ id: number; name: string } | null>(null);
  const [isPackDeleteDialogOpen, setIsPackDeleteDialogOpen] = useState(false);

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskModalPack, setTaskModalPack] = useState<Pack | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Pack editor state now managed internally by PackEditorDialog

  // Fetching data for this tab
  const { data: packs = [], isLoading: packsLoading } = useQuery<Pack[]>({
    queryKey: ["retailers", retailerId, "packs", showArchived],
    queryFn: async () => {
      if (!retailerId) return [];
      const res = await apiFetch(`/api/retailers/${retailerId}/packs?showArchived=${showArchived}`);
      if (!res.ok) throw new Error(`Failed to fetch packs: ${res.statusText}`);
      return res.json();
    },
    enabled: !!retailerId,
    staleTime: 0,
  });

  const { data: stores = [] } = useQuery<StoreType[]>({
    queryKey: ['stores', { retailerId }],
    queryFn: async () => getJson(`/api/retailers/${retailerId}/stores`),
    enabled: !!retailerId,
  });

  const { data: storeMediaRequirements = [] } = useQuery<StoreMediaRequirement[]>({
    queryKey: ['/api/store-media-requirements'],
    queryFn: async () => {
      const res = await apiFetch('/api/store-media-requirements');
      if (!res.ok) {
        throw new Error(`Failed to fetch store media requirements: ${res.statusText}`);
      }
      return res.json();
    },
  });

  const { data: mediaFormats = [] } = useQuery<MediaFormat[]>({
    queryKey: ['/api/media-formats', retailerId],
    queryFn: async () => {
      if (!retailerId) return [];
      const res = await apiFetch(`/api/media-formats?retailerId=${retailerId}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch media formats: ${res.statusText}`);
      }
      return res.json();
    },
    enabled: !!retailerId,
  });

  const retailerMediaFormats = useMemo(() => Array.isArray(mediaFormats) ? mediaFormats : [], [mediaFormats]);

  const { data: allTasks = [] } = useQuery<Task[]>({
    queryKey: ['tasks'],
    queryFn: async () => getJson<Task[]>('/api/tasks'),
  });
  const tasks = allTasks;

  const { data: allPackTaskAssignments = [], isLoading: assignmentsLoading, error: assignmentsError } = useQuery<any[]>({
    queryKey: ['packTaskAssignments', selectedPack?.id],
    queryFn: async () => getJson(`/api/packs/${selectedPack!.id}/task-assignments`),
    enabled: packDialogMode === 'edit' && !!selectedPack,
  });
  const packTaskAssignments = allPackTaskAssignments;

  // Pack mutations
  const deletePackMutation = useMutation<void, Error, number>({
    mutationFn: async (packId) => {
      const res = await apiFetch(`/api/packs/${packId}`, { method: 'DELETE' });
      if (!res.ok) {
        throw new Error(`Failed to delete pack: ${res.statusText}`);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["retailers", retailerId, "packs"], refetchType: 'active' });
      await queryClient.refetchQueries({ queryKey: ["retailers", retailerId, "packs"] });
      toast({ title: "Pack deleted successfully" });
    },
    onError: () => toast({ title: "Failed to delete pack", variant: "destructive" }),
  });

  const assignTaskMutation = useMutation<any, Error, { packId: number; taskId: number; quantity: number }>({
    mutationFn: (data) => apiFetch('/api/pack-task-assignments', { method: 'POST', body: JSON.stringify(data) }).then(res => res.json()),
    onSuccess: () => {
      toast({ title: "Task assigned successfully" });
      queryClient.invalidateQueries({ queryKey: ['packTaskAssignments', taskModalPack?.id] });
    },
    onError: (error) => toast({ title: `Error assigning task: ${error.message}`, variant: "destructive" })
  });

  const removeTaskMutation = useMutation<any, Error, number>({
    mutationFn: (assignmentId) => apiFetch(`/api/pack-task-assignments/${assignmentId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['packTaskAssignments', taskModalPack?.id], refetchType: 'active' });
      await queryClient.refetchQueries({ queryKey: ['packTaskAssignments', taskModalPack?.id] });
      toast({ title: "Task removed successfully" });
    },
    onError: (error) => toast({ title: `Error removing task: ${error.message}`, variant: "destructive" })
  });

  // Handlers
  const handleCreateNewPack = () => {
    console.log('[handleCreateNewPack] Opening PackEditorDialog for new pack');
    setPackDialogMode('create');
    setSelectedPack(null);
    setIsPackDialogOpen(true);
  };

  const handleEditPack = (pack: Pack, mode: 'view' | 'edit' = 'edit') => {
    console.log('[handleEditPack] Opening PackEditorDialog for pack:', pack.id, 'mode:', mode);
    setPackDialogMode(mode);
    setSelectedPack(pack);
    setIsPackDialogOpen(true);
  };



  const handleAssignTask = () => {
    if (taskModalPack && selectedTaskId) {
      assignTaskMutation.mutate({ packId: taskModalPack.id, taskId: selectedTaskId, quantity: 1 });
    }
  };

  const handleRemoveTask = (assignmentId: number) => {
    removeTaskMutation.mutate(assignmentId);
  };

  const handleDeletePack = (pack: Pack) => {
    setPackToDelete({ id: pack.id, name: pack.name });
    setIsPackDeleteDialogOpen(true);
  };

  const confirmDeletePack = () => {
    if (packToDelete) {
      deletePackMutation.mutate(packToDelete.id);
    }
  };

  // Pack creation and updates now handled by PackEditorDialog

  const packColumns = useMemo(
    () => createPackColumns({
      PackLockStatusBadge,
      PackLockToggleButton,
      PackEditButton,
      onEdit: handleEditPack,
      onDelete: handleDeletePack
    }),
    [handleEditPack, handleDeletePack]
  );

  if (packsLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading packs...</div>;
  }

  // Packs Table JSX
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="show-archived"
            checked={showArchived}
            onCheckedChange={(checked) => setShowArchived(!!checked)}
          />
          <label
            htmlFor="show-archived"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
          >
            Show Archived Packs
          </label>
        </div>
        <Button onClick={handleCreateNewPack}>
          <Plus className="h-4 w-4 mr-2" />
          Create Pack Template
        </Button>
      </div>
      {/* Pack Editor Dialog */}
      <PackEditorDialog
        isOpen={isPackDialogOpen}
        onClose={() => setIsPackDialogOpen(false)}
        mode={packDialogMode}
        pack={selectedPack}
        retailerId={retailerId}
        onSuccess={() => {
          // Refresh pack list
          queryClient.invalidateQueries({ queryKey: ["retailers", retailerId, "packs"] });
        }}
      />
      {packs.length > 0 ? (
        <DataTable
          columns={packColumns}
          data={packs}
          searchKey="name"
          searchPlaceholder="Search by pack name..."
          showColumnToggle={true}
          isLoading={packsLoading}
        />
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          No packs configured for this retailer
        </div>
      )}

      {/* Delete Pack Dialog */}
      <AlertDialog open={isPackDeleteDialogOpen} onOpenChange={setIsPackDeleteDialogOpen}>
        <AlertDialogContent className={cn("bg-white")}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pack</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete pack "{packToDelete?.name}"? This action cannot be undone and will remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeletePack}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletePackMutation.isPending}
            >
              {deletePackMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
